import { Section } from '@all-hands-hq/plex-graphql-schema';

export type SelectedSectionsAction =
  | {
      type: 'ROW_CLICK';
      section: Section;
    }
  | {
      type: 'CHECKBOX_CLICK';
      section: Section;
    }
  | {
      type: 'UPDATE_SELECTION';
      selectedSections: Section[];
    };

type SelectedSectionsState = Set<Section>;

export const selectedSectionsReducer = (
  state: SelectedSectionsState,
  action: SelectedSectionsAction
): SelectedSectionsState => {
  switch (action.type) {
    case 'ROW_CLICK': {
      const { section } = action;
      const sectionWithDescendents = new Set(Section.getDescendentsInclusive(section));
      if (!state.has(section) || !setsAreEqual(sectionWithDescendents, state)) {
        return sectionWithDescendents;
      } else {
        return new Set([]);
      }
    }

    case 'CHECKBOX_CLICK': {
      const { section } = action;
      if (state.has(section)) {
        return selectedSectionsWithDelta({
          selectedSections: state,
          addedSections: [],
          removedSections: [section],
        });
      } else {
        return selectedSectionsWithDelta({
          selectedSections: state,
          addedSections: [section],
          removedSections: [],
        });
      }
    }

    case 'UPDATE_SELECTION': {
      const { selectedSections: newSelectedSections } = action;
      const newSelectedSectionsSet = new Set(newSelectedSections);
      const oldSelectedSections = state;
      const oldSelectedSectionsArray = [...oldSelectedSections];

      const addedSections = newSelectedSections.filter((section) => !oldSelectedSections.has(section));
      const removedSections = oldSelectedSectionsArray.filter((section) => !newSelectedSectionsSet.has(section));

      return selectedSectionsWithDelta({
        selectedSections: oldSelectedSections,
        addedSections,
        removedSections,
      });
    }
  }
};

function selectedSectionsWithDelta({
  selectedSections,
  addedSections,
  removedSections,
}: {
  selectedSections: Set<Section>;
  addedSections: Section[];
  removedSections: Section[];
}): Set<Section> {
  // When a node is removed, we must also remove:
  //  - all its descendents (as we are un-selecting a part of the tree)
  //  - all its ancestors (as they are no longer fully selected)
  //
  // Note that its siblings will stay selected
  const extendedRemovedSections = new Set([
    ...removedSections.map(Section.getAncestorsInclusive).flat(),
    ...removedSections.map(Section.getDescendentsInclusive).flat(),
  ]);

  // When a node is added, we must also add
  //  - all its descendents
  const extendedAddedSections = addedSections.map(Section.getDescendentsInclusive).flat();

  return new Set([
    ...Array.from(selectedSections).filter((section) => !extendedRemovedSections.has(section)),
    ...extendedAddedSections,
  ]);
}

function setsAreEqual<T>(a: Set<T>, b: Set<T>): boolean {
  return a.size === b.size && new Set([...a, ...b]).size === a.size;
}
